import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useSubmit — double-submit protection for any async action.
 *
 * Uses a ref (not state) as the in-flight lock so two rapid taps/clicks
 * cannot both slip through before a state update renders.
 *
 * Usage:
 *   const { run, isLoading } = useSubmit();
 *   <Button loading={isLoading} onClick={() => run(async () => { await api.save(data); })}>Save</Button>
 */
export function useSubmit() {
  const [isLoading, setIsLoading] = useState(false);
  const inFlight   = useRef(false);   // synchronous lock — state is async, ref is instant
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const run = useCallback(async (fn) => {
    if (inFlight.current) return;   // block second tap immediately
    inFlight.current = true;
    if (mountedRef.current) setIsLoading(true);
    try {
      await fn();
    } finally {
      inFlight.current = false;
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  return { run, isLoading };
}
